import jsPDF from 'jspdf';

const text = node => {
  if (node.nodeType === 3) return node.textContent;
  if (node.classList?.contains('MuiChip-root')) {
    const label = node.getAttribute('aria-label') || node.getAttribute('title');
    if (label) return label;
  }
  return [...node.childNodes].map(text).join(' ').replace(/\s+/g, ' ').trim();
};

export function teamStatisticsPdfBlocks(root) {
  const blocks = [];
  const visit = node => {
    if (node.nodeType !== 1 || node.getAttribute('aria-hidden') === 'true' ||
      ['SVG', 'BUTTON', 'IMG'].includes(node.tagName)) return;
    if (/^H[1-6]$/.test(node.tagName)) {
      blocks.push({ type: 'heading', text: text(node), level: Number(node.tagName[1]) });
    } else if (node.tagName === 'TABLE') {
      blocks.push({
        type: 'table', title: node.getAttribute('aria-label'),
        headers: [...node.querySelectorAll('thead tr:first-child th')].map(text),
        widths: node.dataset.pdfWidths?.split(',').map(Number),
        rows: [...node.querySelectorAll('tbody tr')].map(row => [...row.cells].map(text)),
      });
    } else if (node.tagName === 'DL') {
      const rows = [...node.querySelectorAll('dt')].map(label => {
        const value = label.parentElement.querySelector('dd');
        return [text(label), [value, ...[...label.parentElement.children]
          .filter(child => child !== value && child.matches('small, p'))].filter(Boolean).map(text).join(' ')];
      });
      if (rows.length) blocks.push({ type: 'table', headers: [], rows });
    } else if (node.tagName === 'P' || node.matches('.ts-context, .ts-form li')) {
      blocks.push({ type: 'paragraph', text: text(node) });
    } else {
      [...node.children].forEach(visit);
    }
  };
  [...root.children].forEach(visit);
  return blocks;
}

export function createTeamStatisticsPdf(root, { teamName, season, language, pageLabel }) {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
  const width = pdf.internal.pageSize.getWidth();
  const height = pdf.internal.pageSize.getHeight();
  const margin = 14;
  const usable = width - margin * 2;
  const bottom = height - 16;
  let y;
  const clean = value => String(value ?? '').replace(/[\u00a0\u202f]/g, ' ').replace(/\u2032/g, "'");
  const font = (size, bold = false) => { pdf.setFont('helvetica', bold ? 'bold' : 'normal'); pdf.setFontSize(size); };
  const lines = (value, space) => pdf.splitTextToSize(clean(value), space);
  const page = (add = true) => {
    if (add) pdf.addPage();
    pdf.setFillColor('#163c2a');
    pdf.rect(0, 0, width, 25, 'F');
    pdf.setTextColor('#c7f36b');
    font(13, true);
    pdf.text('SOCCERTYCS', margin, 11);
    font(8);
    pdf.setTextColor('#ffffff');
    pdf.text(lines(teamName, usable)[0], margin, 18);
    pdf.text(String(season ?? ''), width - margin, 11, { align: 'right' });
    pdf.setTextColor('#172b21');
    y = 34;
  };
  const space = size => { if (y + size > bottom) page(); };
  const paragraph = (value, level) => {
    const size = level ? level <= 2 ? 14 : 11 : 9;
    const leading = level ? 6 : 4.5;
    font(size, Boolean(level));
    const wrapped = lines(value, usable);
    space(Math.min(wrapped.length * leading + 5, 25));
    if (level) { y += 3; pdf.setTextColor('#167345'); }
    wrapped.forEach(line => {
      space(leading);
      font(size, Boolean(level));
      pdf.setTextColor(level ? '#167345' : '#344d40');
      pdf.text(line, margin, y);
      y += leading;
    });
    y += level ? 3 : 2;
  };
  page(false);
  pdf.setProperties({ title: `${teamName} - ${season ?? ''}`, author: 'Soccertycs',
    subject: new Intl.DateTimeFormat(language, { dateStyle: 'long' }).format(new Date()) });
  let lastHeading = '';
  teamStatisticsPdfBlocks(root).forEach(block => {
    if (block.type !== 'table') {
      paragraph(block.text, block.type === 'heading' ? block.level : null);
      if (block.type === 'heading') lastHeading = block.text;
      return;
    }
    if (block.title && block.title !== lastHeading) {
      paragraph(block.title, 3);
      lastHeading = block.title;
    }
    const columns = Math.max(block.headers.length, ...block.rows.map(row => row.length), 1);
    const firstWidth = columns === 2 ? usable * .64 : usable * .36;
    const weights = Array.from({ length: columns - 1 }, (_, index) => Math.max(6, block.headers[index + 1]?.length || 6));
    const weightTotal = weights.reduce((sum, value) => sum + value, 0);
    const customWidths = block.widths?.length === columns && block.widths.every(value => Number.isFinite(value) && value > 0)
      ? block.widths.map(value => usable * value / block.widths.reduce((sum, part) => sum + part, 0)) : null;
    const widths = customWidths || Array.from({ length: columns }, (_, index) =>
      columns === 1 ? usable : index === 0 ? firstWidth : (usable - firstWidth) * weights[index - 1] / weightTotal);
    const prepare = (row, header) => {
      font(header ? 8 : 9, header);
      const rowWidths = row.length === 1 ? [usable] : widths;
      const cells = rowWidths.map((cellWidth, index) => lines(row[index] ?? '', cellWidth - 6));
      return { cells, widths: rowWidths, rowHeight: Math.max(8, ...cells.map(cell => cell.length * 4.2 + 4)) };
    };
    const draw = (prepared, header, shaded) => {
      let x = margin;
      prepared.cells.forEach((cell, index) => {
        pdf.setFillColor(header ? '#d9efb2' : shaded ? '#f0f5f1' : '#ffffff');
        pdf.rect(x, y, prepared.widths[index], prepared.rowHeight, 'F');
        pdf.setDrawColor('#dbe5de');
        pdf.line(x, y + prepared.rowHeight, x + prepared.widths[index], y + prepared.rowHeight);
        font(header ? 8 : 9, header);
        pdf.setTextColor('#172b21');
        pdf.text(cell, x + 3, y + 5);
        x += prepared.widths[index];
      });
      y += prepared.rowHeight;
    };
    const header = block.headers.length ? prepare(block.headers, true) : null;
    if (header) { space(header.rowHeight + 12); draw(header, true, false); }
    block.rows.forEach((row, index) => {
      const prepared = prepare(row, false);
      if (y + prepared.rowHeight > bottom) {
        page();
        if (header) draw(header, true, false);
      }
      draw(prepared, false, index % 2 === 0);
    });
    y += 6;
  });
  const total = pdf.getNumberOfPages();
  for (let current = 1; current <= total; current += 1) {
    pdf.setPage(current);
    font(8);
    pdf.setTextColor('#526b5c');
    pdf.text(pageLabel(current, total), width - margin, height - 8, { align: 'right' });
  }
  return pdf;
}
